import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FaBriefcase,
  FaGraduationCap,
  FaPlaneDeparture,
  FaBuilding,
  FaCheckCircle,
  FaWhatsapp,
  FaPhoneAlt,
  FaSearch,
  FaGlobe,
} from "react-icons/fa";
import CountUp from "react-countup";

const ALL_COUNTRIES = [
  {
    code: "ca",
    name: "Canada",
    continent: "North America",
    visaTypes: ["Work Visa", "Student Visa", "PR Visa"],
    highlight: "Express Entry & PNP Programs",
    processingTime: "3–6 months",
    popular: true,
  },
  {
    code: "gb",
    name: "United Kingdom",
    continent: "Europe",
    visaTypes: ["Skilled Worker Visa", "Student Visa", "Business Visa"],
    highlight: "Skilled Worker Visa Route",
    processingTime: "3–8 weeks",
    popular: true,
  },
  {
    code: "au",
    name: "Australia",
    continent: "Oceania",
    visaTypes: ["Skilled Migration", "Student Visa", "Work Visa"],
    highlight: "Points-Based Skilled Migration",
    processingTime: "4–8 months",
    popular: true,
  },
  {
    code: "us",
    name: "United States",
    continent: "North America",
    visaTypes: ["H-1B Work Visa", "Student Visa", "Tourist Visa"],
    highlight: "H-1B & Green Card Routes",
    processingTime: "2–12 months",
    popular: true,
  },
  {
    code: "de",
    name: "Germany",
    continent: "Europe",
    visaTypes: ["Job Seeker Visa", "Student Visa", "Work Visa"],
    highlight: "EU Blue Card Program",
    processingTime: "4–12 weeks",
    popular: true,
  },
  {
    code: "ae",
    name: "UAE",
    continent: "Middle East",
    visaTypes: ["Work Visa", "Business Visa", "Golden Visa"],
    highlight: "Golden Visa & Business Setup",
    processingTime: "2–4 weeks",
    popular: true,
  },
  {
    code: "nz",
    name: "New Zealand",
    continent: "Oceania",
    visaTypes: ["Skilled Migrant", "Work Visa", "Student Visa"],
    highlight: "Skilled Migrant Category",
    processingTime: "3–6 months",
    popular: false,
  },
  {
    code: "sg",
    name: "Singapore",
    continent: "Asia",
    visaTypes: ["Employment Pass", "Entrepreneur Pass", "Student Pass"],
    highlight: "Employment Pass Scheme",
    processingTime: "3–8 weeks",
    popular: false,
  },
  {
    code: "fr",
    name: "France",
    continent: "Europe",
    visaTypes: ["Work Visa", "Student Visa", "Long-Stay Visa"],
    highlight: "Tech Visa & Talent Passport",
    processingTime: "4–12 weeks",
    popular: false,
  },
  {
    code: "it",
    name: "Italy",
    continent: "Europe",
    visaTypes: ["Work Visa", "Student Visa", "Business Visa"],
    highlight: "Decreto Flussi Work Permits",
    processingTime: "4–10 weeks",
    popular: false,
  },
  {
    code: "pt",
    name: "Portugal",
    continent: "Europe",
    visaTypes: ["Golden Visa", "D7 Visa", "Student Visa"],
    highlight: "Golden Visa & D7 Passive Income",
    processingTime: "2–4 months",
    popular: false,
  },
  {
    code: "nl",
    name: "Netherlands",
    continent: "Europe",
    visaTypes: ["Highly Skilled Migrant", "Student Visa", "Business Visa"],
    highlight: "Highly Skilled Migrant Permit",
    processingTime: "2–4 weeks",
    popular: false,
  },
  {
    code: "se",
    name: "Sweden",
    continent: "Europe",
    visaTypes: ["Work Permit", "Student Visa", "EU Blue Card"],
    highlight: "EU Blue Card & Work Permit",
    processingTime: "1–4 months",
    popular: false,
  },
  {
    code: "ie",
    name: "Ireland",
    continent: "Europe",
    visaTypes: ["Critical Skills Visa", "Work Permit", "Student Visa"],
    highlight: "Critical Skills Employment Permit",
    processingTime: "4–6 weeks",
    popular: false,
  },
  {
    code: "my",
    name: "Malaysia",
    continent: "Asia",
    visaTypes: ["Work Visa", "MM2H Visa", "Student Visa"],
    highlight: "Malaysia My Second Home (MM2H)",
    processingTime: "3–6 months",
    popular: false,
  },
  {
    code: "mt",
    name: "Malta",
    continent: "Europe",
    visaTypes: ["Work Visa", "Residency by Investment", "Student Visa"],
    highlight: "EU Residency & Citizenship Path",
    processingTime: "2–4 months",
    popular: false,
  },
];

const CONTINENTS = ["All", "Europe", "North America", "Asia", "Middle East", "Oceania"];

const VISA_ICONS = {
  "Work Visa": FaBriefcase,
  "Student Visa": FaGraduationCap,
  "Tourist Visa": FaPlaneDeparture,
  "Business Visa": FaBuilding,
  "PR Visa": FaCheckCircle,
  Default: FaGlobe,
};

function Countries() {
  const [search, setSearch] = useState("");
  const [activeContinent, setActiveContinent] = useState("All");

  const filtered = ALL_COUNTRIES.filter((c) => {
    const matchesSearch =
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.visaTypes.some((v) => v.toLowerCase().includes(search.toLowerCase()));
    const matchesContinent =
      activeContinent === "All" || c.continent === activeContinent;
    return matchesSearch && matchesContinent;
  });

  const popular = ALL_COUNTRIES.filter((c) => c.popular);

  return (
    <div>
      <Helmet>
        <title>Countries We Serve | Lumis Documents Clearing Dubai</title>
        <meta name="description" content="Explore immigration options for Canada, UK, Australia, USA, Germany, UAE, and more. Find your perfect visa pathway with Lumis." />
      </Helmet>
      {/* HERO */}
      <section
        className="h-[420px] flex items-center justify-center text-white bg-cover bg-center relative"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative text-center px-6">
          <p className="tracking-widest text-blue-400 text-sm mb-3 uppercase">
            Global Immigration
          </p>

          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Countries We Serve
          </h1>

          <p className="text-gray-200 max-w-xl mx-auto mb-4">
            Expert visa and immigration support for top global destinations.
            Wherever you want to go, Lumis gets you there.
          </p>

          <div className="text-gray-300 text-sm">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-white font-medium">Countries</span>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-blue-600 text-white py-10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { end: 16, suffix: "+", label: "Countries Covered" },
            { end: 1200, suffix: "+", label: "Visas Processed" },
            { end: 98, suffix: "%", label: "Success Rate" },
            { end: 800, suffix: "+", label: "Happy Clients" },
          ].map(({ end, suffix, label }) => (
            <div key={label}>
              <h3 className="text-3xl font-bold">
                <CountUp end={end} duration={2.5} enableScrollSpy scrollSpyDelay={200} />
                {suffix}
              </h3>
              <p className="text-blue-100 text-sm mt-1">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* POPULAR DESTINATIONS */}
      <section className="py-24 bg-white relative overflow-hidden">
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg"
          className="absolute opacity-5 w-full top-10 pointer-events-none"
          alt="world map background"
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <h2 className="text-3xl font-bold text-center mb-3">
            Popular Destinations
          </h2>
          <p className="text-center text-gray-500 mb-14">
            Our most sought-after immigration destinations
          </p>

          <div className="grid md:grid-cols-3 lg:grid-cols-3 gap-8">
            {popular.map((country) => (
              <div
                key={country.code}
                className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition duration-300 border border-gray-100"
              >
                {/* COUNTRY BANNER */}
                <div
                  className="h-40 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url('https://source.unsplash.com/600x300/?${country.name},landmark,city')`,
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70" />

                  {/* FLAG */}
                  <img
                    src={`https://flagcdn.com/w160/${country.code}.png`}
                    alt={country.name}
                    className="absolute bottom-3 left-4 h-10 w-14 object-cover rounded shadow-lg border-2 border-white"
                  />

                  <span className="absolute top-3 right-3 bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-medium">
                    {country.continent}
                  </span>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold mb-1 group-hover:text-blue-600 transition">
                    {country.name}
                  </h3>
                  <p className="text-sm text-blue-600 font-medium mb-3">
                    ✦ {country.highlight}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {country.visaTypes.map((v) => (
                      <span
                        key={v}
                        className="bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full font-medium"
                      >
                        {v}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-500 border-t pt-4">
                    <span>⏱ Processing: {country.processingTime}</span>
                    <Link
                      to="/contact"
                      className="text-blue-600 font-semibold hover:underline"
                    >
                      Apply →
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ALL COUNTRIES — SEARCHABLE & FILTERABLE */}
      <section className="py-24 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">
            All Destinations
          </h2>
          <p className="text-center text-gray-500 mb-10">
            Search by country name or visa type, then filter by region
          </p>

          {/* SEARCH + FILTER */}
          <div className="flex flex-col md:flex-row gap-4 mb-10">
            {/* SEARCH INPUT */}
            <div className="relative flex-1">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search country or visa type..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
            </div>

            {/* CONTINENT FILTER */}
            <div className="flex flex-wrap gap-2">
              {CONTINENTS.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveContinent(c)}
                  className={`px-5 py-2.5 rounded-xl font-medium text-sm transition ${
                    activeContinent === c
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-white text-gray-600 border border-gray-200 hover:bg-blue-50 hover:text-blue-600"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* RESULTS COUNT */}
          <p className="text-gray-500 text-sm mb-6">
            Showing <span className="font-semibold text-gray-800">{filtered.length}</span> destination{filtered.length !== 1 ? "s" : ""}
          </p>

          {/* COUNTRY GRID */}
          {filtered.length > 0 ? (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filtered.map((country) => (
                <div
                  key={country.code}
                  className="bg-white rounded-xl p-6 text-center shadow hover:shadow-xl hover:-translate-y-2 transition duration-300 border border-gray-100"
                >
                  <img
                    src={`https://flagcdn.com/w320/${country.code}.png`}
                    alt={country.name}
                    className="h-16 w-24 mx-auto mb-4 object-cover rounded-lg shadow border border-gray-200"
                  />

                  <h3 className="font-bold text-lg mb-1">{country.name}</h3>
                  <p className="text-xs text-blue-600 font-medium mb-3">
                    {country.continent}
                  </p>

                  <div className="flex flex-col gap-1.5 mb-4 text-left">
                    {country.visaTypes.slice(0, 2).map((v) => {
                      const Icon = VISA_ICONS[v] || VISA_ICONS.Default;
                      return (
                        <div
                          key={v}
                          className="flex items-center gap-2 text-xs text-gray-600"
                        >
                          <Icon className="text-blue-500 flex-shrink-0" />
                          <span>{v}</span>
                        </div>
                      );
                    })}
                    {country.visaTypes.length > 2 && (
                      <p className="text-xs text-gray-400 ml-5">
                        +{country.visaTypes.length - 2} more
                      </p>
                    )}
                  </div>

                  <Link
                    to="/contact"
                    className="block w-full bg-blue-50 hover:bg-blue-600 text-blue-600 hover:text-white text-sm font-semibold py-2 rounded-lg transition"
                  >
                    Get Consultation
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <FaGlobe className="text-5xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                No destinations match your search.
              </p>
              <button
                onClick={() => {
                  setSearch("");
                  setActiveContinent("All");
                }}
                className="mt-4 text-blue-600 font-semibold hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* VISA TYPES WE HANDLE */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">
            Visa Types We Handle
          </h2>
          <p className="text-center text-gray-500 mb-14">
            Professional guidance across all major visa categories
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                Icon: FaBriefcase,
                title: "Work Visas",
                desc: "Employment, H-1B, Skilled Worker and Blue Card visas for top global markets.",
              },
              {
                Icon: FaGraduationCap,
                title: "Student Visas",
                desc: "Tier 4, F-1, and international student visas for universities worldwide.",
              },
              {
                Icon: FaBuilding,
                title: "Business Visas",
                desc: "Investor, entrepreneur and corporate visas to expand your business globally.",
              },
              {
                Icon: FaPlaneDeparture,
                title: "Travel & Tourist",
                desc: "Short-stay tourist and visit visas for leisure and family travel.",
              },
            ].map(({ Icon, title, desc }) => (
              <div
                key={title}
                className="group text-center bg-white border border-gray-100 rounded-xl p-8 shadow-md hover:shadow-2xl hover:-translate-y-2 transition duration-300"
              >
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-5 group-hover:bg-blue-600 transition duration-300">
                  <Icon className="text-blue-600 text-2xl group-hover:text-white transition duration-300" />
                </div>
                <h3 className="font-bold text-lg mb-3 group-hover:text-blue-600 transition">
                  {title}
                </h3>
                <p className="text-gray-500 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">How It Works</h2>
          <p className="text-center text-gray-500 mb-14">
            Simple steps to your dream destination
          </p>

          <div className="grid md:grid-cols-4 gap-8 text-center relative">
            {/* CONNECTING LINE */}
            <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-blue-100 z-0" />

            {[
              { step: "01", title: "Choose Country", desc: "Select your target destination and visa category." },
              { step: "02", title: "Free Consultation", desc: "Speak with our experts to assess your eligibility." },
              { step: "03", title: "Document Prep", desc: "We guide you through every document required." },
              { step: "04", title: "Visa Approved!", desc: "We submit your application and track it to approval." },
            ].map(({ step, title, desc }) => (
              <div key={step} className="relative z-10">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-5 shadow-lg">
                  {step}
                </div>
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p className="text-gray-500 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-600 text-white py-20 text-center">
        <h2 className="text-3xl font-bold mb-4">
          Ready to Start Your Journey?
        </h2>
        <p className="mb-8 text-blue-100 max-w-xl mx-auto">
          Contact Lumis Documents Clearing for expert visa consultation and
          professional immigration support tailored to your destination.
        </p>

        <div className="flex justify-center gap-4 flex-wrap">
          <Link
            to="/contact"
            className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition shadow-md"
          >
            Get Free Consultation
          </Link>
          <a
            href="https://wa.me/971547662665"
            target="_blank"
            rel="noopener noreferrer"
            className="border border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition flex items-center gap-2"
          >
            <FaWhatsapp /> WhatsApp Us
          </a>
        </div>
      </section>

      {/* FLOATING CONTACT WIDGET */}
      <div className="fixed bottom-6 right-6 flex flex-col items-end gap-4 z-50">
        <div className="bg-white shadow-xl rounded-lg p-4 max-w-xs animate-bounce">
          <p className="text-sm text-gray-700">💬 Need help with your visa?</p>
          <p className="text-sm font-semibold text-gray-900">
            Chat with us on WhatsApp
          </p>
        </div>

        <Link to="/contact" className="group flex items-center">
          <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
            Free Consultation
          </span>
          <div className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
            <FaPhoneAlt size={20} />
          </div>
        </Link>

        <a
          href="https://wa.me/971547662665"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center"
        >
          <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
            Chat on WhatsApp
          </span>
          <div className="bg-green-500 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
            <FaWhatsapp size={22} />
          </div>
        </a>
      </div>
    </div>
  );
}

export default Countries;
